import { useRef, useState } from "react";
import { Link } from "react-router";
import ReactJson from "react-json-view";
import { streamChat, uploadAttachment } from "../../utils/agentsApi";
import "../provider/ProviderGuide.scss";
import "../home/HomeGuide.scss";
import "./InternalPoc.scss";

const ACCEPT = ".xlsx,.xls,.csv,.pdf,.docx,.txt,.json";

const PROMPT = [
    "Build two documents from the attached files.",
    "Call emit_benefit_plan once for every plan sheet. Good Times has Partnership Plan, Traditional PPO, and Traditional EPO.",
    "emit_account_details is contacts, address, stop-loss, and utilization management.",
    "emit_account_proposal follows account-proposal.json and lists every plan.",
    "Put CPT and revenue codes on the matching inNetwork service.",
    "Omit fields the files do not state.",
].join(" ");

const parseCanonical = (result) => {
    if (!result) return null;
    if (typeof result === "object") return result;
    if (typeof result !== "string") return null;
    try {
        return JSON.parse(result);
    } catch {
        return null;
    }
};

const Result = ({ title, document }) => (
    <section className="internal-poc__result">
        <header>
            <h2>{title}</h2>
            <button
                type="button"
                onClick={() => navigator.clipboard.writeText(JSON.stringify(document, null, 2))}
            >
                Copy JSON
            </button>
        </header>
        <div className="internal-poc__json">
            <ReactJson
                src={document}
                name={false}
                collapsed={2}
                collapseStringsAfterLength={120}
                displayDataTypes={false}
                displayObjectSize
                enableClipboard={false}
                sortKeys={false}
                quotesOnKeys={false}
            />
        </div>
    </section>
);

const InternalPoc = () => {
    const inputRef = useRef(null);
    const filesRef = useRef([]);
    const [files, setFiles] = useState([]);
    const [dragOver, setDragOver] = useState(false);
    const [status, setStatus] = useState("");
    const [error, setError] = useState("");
    const [documents, setDocuments] = useState({ account: null, proposal: null });
    const [running, setRunning] = useState(false);

    const addFiles = (list) => {
        const next = [...list].filter(Boolean);
        if (!next.length) return;
        const seen = new Set(filesRef.current.map((file) => `${file.name}:${file.size}`));
        const unique = next.filter((file) => !seen.has(`${file.name}:${file.size}`));
        if (!unique.length) return;
        filesRef.current = [...filesRef.current, ...unique];
        setFiles(filesRef.current);
        setError("");
    };

    const build = async () => {
        if (!files.length || running) return;
        setRunning(true);
        setError("");
        setDocuments({ account: null, proposal: null });
        setStatus("Uploading files");
        const controller = new AbortController();
        try {
            const attachments = [];
            for (const file of files) {
                setStatus(`Uploading ${file.name}`);
                attachments.push(await uploadAttachment(file));
            }
            setStatus("Reading the files");
            for await (const event of streamChat({
                agentId: "canonicalAccountAgent",
                message: PROMPT,
                attachments,
                signal: controller.signal,
            })) {
                if (event.type === "tool_call") {
                    if (event.name === "emit_benefit_plan") setStatus("Writing a benefit plan");
                    else if (event.name === "emit_account_details") setStatus("Writing account details");
                    else if (event.name === "emit_account_proposal") setStatus("Writing the proposal");
                    else setStatus(`Working · ${event.name}`);
                }
                if (event.type === "tool_result") {
                    const parsed = parseCanonical(event.result);
                    if (!parsed) continue;
                    if (event.name === "emit_benefit_plan") {
                        setDocuments((prev) => {
                            const account = prev.account || {};
                            const plans = [...(account.benefitPlans || [])];
                            const name = parsed.planName;
                            const index = plans.findIndex((plan) => plan.planName === name);
                            if (index >= 0) plans[index] = parsed;
                            else plans.push(parsed);
                            return { ...prev, account: { ...account, benefitPlans: plans } };
                        });
                        if (parsed.planName) setStatus(`Wrote ${parsed.planName}`);
                    }
                    if (event.name === "emit_account_details") {
                        setDocuments((prev) => ({
                            ...prev,
                            account: {
                                ...parsed,
                                benefitPlans: prev.account?.benefitPlans || [],
                            },
                        }));
                    }
                    if (event.name === "emit_account_proposal") {
                        setDocuments((prev) => ({ ...prev, proposal: parsed }));
                    }
                }
                if (event.type === "error") {
                    throw new Error(event.message || "The agent stopped");
                }
            }
            setStatus("Done");
        } catch (err) {
            setError(err.message || "Could not build the account");
            setStatus("");
        } finally {
            setRunning(false);
        }
    };

    return (
        <div className="container provider-guide home-guide internal-poc">
            <div className="provider-guide__frame">
                <header className="provider-guide__intro">
                    <p className="provider-guide__eyebrow">Internal · POC</p>
                    <h1>Two documents from the files</h1>
                    <p>
                        Drop the workbooks for a single employer. A deep agent
                        writes account details in the account-details shape, and
                        a separate proposal in the account-proposal shape.
                    </p>
                </header>

                <div
                    className={`internal-poc__drop${dragOver ? " is-over" : ""}`}
                    onDragOver={(event) => {
                        event.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(event) => {
                        event.preventDefault();
                        setDragOver(false);
                        addFiles(event.dataTransfer.files);
                    }}
                    onClick={() => inputRef.current?.click()}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") inputRef.current?.click();
                    }}
                    role="button"
                    tabIndex={0}
                >
                    <strong>Drop proposal, benefit summary, MCW, and config grid</strong>
                    <span>xlsx, csv, pdf, docx, or json</span>
                    <input
                        ref={inputRef}
                        type="file"
                        accept={ACCEPT}
                        multiple
                        hidden
                        onChange={(event) => {
                            addFiles(event.target.files);
                            event.target.value = "";
                        }}
                    />
                </div>

                {files.length > 0 && (
                    <ul className="internal-poc__files">
                        {files.map((file) => (
                            <li key={`${file.name}:${file.size}`}>
                                <span>{file.name}</span>
                                <button
                                    type="button"
                                    onClick={() => {
                                        filesRef.current = filesRef.current.filter((row) => row !== file);
                                        setFiles(filesRef.current);
                                    }}
                                    disabled={running}
                                >
                                    Remove
                                </button>
                            </li>
                        ))}
                    </ul>
                )}

                <div className="internal-poc__actions">
                    <button type="button" onClick={build} disabled={!files.length || running}>
                        {running ? "Building" : "Build canonical account"}
                    </button>
                    {status && <p>{status}</p>}
                </div>

                {error && <p className="internal-poc__error">{error}</p>}

                {documents.account && (
                    <Result title="Account details" document={documents.account} />
                )}
                {documents.proposal && (
                    <Result title="Proposal" document={documents.proposal} />
                )}

                <p className="home-guide__hint">
                    <Link to="/internal/guide">How the desks hand off</Link>
                </p>
            </div>
        </div>
    );
};

export default InternalPoc;
